import { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { Alert, AlertIcon, AlertTitle, AlertDescription, Skeleton, Text, Box, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import useCommonToast from '../hooks/useCommonToast';
import useHistoryPush from '../hooks/useHistoryPush';

import {
    getItems,
    getItemType,
    addItemNextToParent,
    sortItemsByDescription,
    copyItem,
    findItem,
    addItemToPortfolios,
} from '../services/items';

import { ReactComponent as AddIcon } from '../icons/add.svg';
import { ReactComponent as BatteryIcon } from '../icons/item-icons/battery.svg';

import LayoutSidebar from '../components/layout/LayoutSidebar';
import LayoutWrapper from '../components/layout/LayoutWrapper';
import ItemListWrapper from '../components/layout/ItemListWrapper';
import OptimizedItemLinkWrapper from '../components/utils/OptimizedItemLinkWrapper';
import AddItemPortfolios from '../components/utils/AddItemPortfolios';
import AddItemModal from '../components/utils/AddItemModal';
import AddItemButton from '../components/utils/AddItemButton';
import VirtualisedItemList from '../components/utils/VirtualisedItemList';

import { SUBSCHEMACODE_BATTERY } from 'constants/uploadDownload';

const Batteries = () => {
    const { itemTypes } = useSelector((state) => state.item);
    const { itemTypeId } = getItemType(itemTypes, 'Battery Storage') || {};
    const { isSidebarVisible } = useSelector((state) => state.helpers);

    const { toast } = useCommonToast();
    const intl = useIntl();
    const historyPush = useHistoryPush();
    const addModal = useDisclosure();
    const addBatteryItemModal = useDisclosure();

    const [batteries, setBatteries] = useState([]);
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [temporaryNode, setTemporaryNode] = useState(null);

    useEffect(() => {
        if (itemTypes.length > 0) {
            getItems(itemTypeId)
                .then((data) => setBatteries(data))
                .catch(() => setLoadingError(true))
                .finally(() => setIsLoading(false));
        }
    }, [itemTypeId, itemTypes]);

    useEffect(() => {
        const term = search.trimStart().toLowerCase();
        const filteredData = batteries.filter((o) => o.description.toLowerCase().includes(term));
        setSearchResults(filteredData);
    }, [batteries, search]);

    const ItemType = itemTypeId ? true : false;

    // @todo don't recalculate the itemIndex on each rerender - useMemo
    const itemIndex = searchResults.findIndex((item) => item.id === selected?.id);

    const noSearchResults = useMemo(() => {
        return search.trim().length > 0 && searchResults.length === 0;
    }, [search, searchResults]);

    const onBatteryClick = useCallback((battery) => {
        if (battery) {
            setSelected(battery);
        }
    }, []);

    const onSearchChange = (value) => {
        setSearch(value);
    };

    const onBatteryCopy = async ({ id }) => {
        toast({ status: 'info', message: intl.formatMessage({ id: 'common_copy_btn_progress_message' }) });

        const temporaryBattery = await copyItem(id);
        setBatteries((prev) => addItemNextToParent(prev, id, temporaryBattery));

        historyPush(`${temporaryBattery.id}`);
    };

    const onAddModalOpen = ({ id }) => {
        setTemporaryNode(findItem(batteries, id));
        addModal.onOpen();
    };

    const closeAddModal = () => {
        addModal.onClose();
        setTemporaryNode(null);
    };

    const getAddItemSuccessMessage = (item, portfolios) => {
        if (portfolios.length === 1) {
            return intl.formatMessage(
                { id: 'common_add_item_success_message' },
                { item: item.description, destination: portfolios[0].properties?.name }
            );
        }

        return intl.formatMessage(
            { id: 'common_add_item_to_multiple_portfolios_success_message' },
            { item: item.description, n: portfolios.length }
        );
    };

    const onAddSuccess = (selectedPortfolios) => {
        toast(getAddItemSuccessMessage(temporaryNode, selectedPortfolios));
        closeAddModal();
    };

    const onBatteryEdit = (selectedBattery) => {
        const updatedBatteries = batteries.map((battery) => {
            if (battery.id === selectedBattery.id) {
                return { ...selectedBattery };
            }
            return battery;
        });
        setBatteries(updatedBatteries);
    };

    const updateBatteries = (newBatteryItem) => {
        setBatteries(sortItemsByDescription([...batteries, newBatteryItem]));
        setSelected(newBatteryItem);
    };

    const onBatteryDelete = (deletedBattery) => {
        const updatedBatteries = batteries.filter((battery) => battery.id !== deletedBattery.id);
        setBatteries(updatedBatteries);
    };

    return (
        <LayoutWrapper
            sidebar={
                isSidebarVisible && (
                    <LayoutSidebar
                        itemType={'battery'}
                        selected={selected}
                        titleId={'battery-title'}
                        titleMessage={intl.formatMessage({ id: 'batteries_title' })}
                        accordionPlaceholderId={'batteries_select_item'}
                        searchPlaceholderId={'common_search_batteries'}
                        searchCallback={onSearchChange}
                        searchTerm={search}
                    >
                        <ItemListWrapper>
                            <Box px={6} pb={2}>
                                <AddItemButton leftIcon={<AddIcon />} onClick={addBatteryItemModal.onOpen}>
                                    <Text size="sm" color="gray.900" pl={1} pt={1}>
                                        <FormattedMessage id="batteries_add_new_item" />
                                    </Text>
                                </AddItemButton>

                                {isLoading ? (
                                    <Skeleton>
                                        <Box h="34px">
                                            <FormattedMessage id="common_loading" />
                                        </Box>
                                    </Skeleton>
                                ) : loadingError ? (
                                    <Alert status="error">
                                        <AlertIcon />
                                        <Box>
                                            <AlertTitle textTransform="capitalize">
                                                <FormattedMessage id="common_error" />
                                            </AlertTitle>

                                            <AlertDescription>
                                                <FormattedMessage
                                                    id="common_loading_error"
                                                    values={{ items: 'batteries' }}
                                                />
                                            </AlertDescription>
                                        </Box>
                                    </Alert>
                                ) : noSearchResults ? (
                                    <Alert status="info">
                                        <AlertIcon />
                                        <FormattedMessage id="common_no_search_results" />
                                    </Alert>
                                ) : null}
                            </Box>
                            {!noSearchResults && !loadingError && !isLoading && (
                                <VirtualisedItemList
                                    items={searchResults}
                                    onCopy={onBatteryCopy}
                                    itemIndex={itemIndex}
                                    itemType={ItemType}
                                    onAddToPortfolioModalOpen={onAddModalOpen}
                                    renderItems={(battery) => (
                                        <OptimizedItemLinkWrapper
                                            icon={BatteryIcon}
                                            onClick={onBatteryClick}
                                            item={battery}
                                        />
                                    )}
                                />
                            )}
                        </ItemListWrapper>
                    </LayoutSidebar>
                )
            }
            content={<Outlet context={{ typeId: itemTypeId, onBatteryEdit, onBatteryDelete }} />}
        >
            {addModal.isOpen && (
                <AddItemPortfolios
                    onClose={closeAddModal}
                    sourceId={temporaryNode.id}
                    itemName={temporaryNode.description}
                    onAddSuccess={onAddSuccess}
                    onSubmit={addItemToPortfolios}
                />
            )}

            {addBatteryItemModal.isOpen && (
                <AddItemModal
                    isOpen
                    updateItems={updateBatteries}
                    onClose={addBatteryItemModal.onClose}
                    itemTypeId={itemTypeId}
                    typeLabel="battery"
                    starterDataAutoFormSchemaCode={SUBSCHEMACODE_BATTERY}
                />
            )}
        </LayoutWrapper>
    );
};

export default Batteries;
